import React, { useMemo } from 'react';
import {
  Globe,
  Palette,
  Trophy,
  HeartHandshake,
  Users,
  Briefcase,
  Gamepad2,
  Plane,
  Globe as InternationalIcon,
  Scissors,
  BookOpen,
  Heart,
  Landmark,
  GraduationCap,
  Mountain,
  Megaphone,
  Code2,
  Paintbrush,
  Activity,
  Medal,
  Dumbbell,
  Music,
  Camera,
  Microscope,
  Utensils,
  Coffee,
  Trees,
  Church,
  Vote,
  Sparkles,
  Lightbulb,
  Calculator,
  Languages,
  Theater,
  Clapperboard,
  Newspaper,
  PenLine,
  Hammer,
  Cpu,
  Stethoscope,
  Rocket,
  Sailboat,
  Fish,
  Dog,
  Flower2,
  UserRound,
  Mic2,
  PartyPopper,
  ShoppingBag,
  Car,
  Bike,
  Snowflake,
  Waves,
} from 'lucide-react';

/** Matches WorldMapCopy event legend ICON_META (FILTER EVENTS BY ICON) */
export const EVENT_LEGEND_COLORS = {
  music: '#ec4899',
  microphone: '#f59e0b',
  sports: '#22c55e',
  arts: '#a855f7',
  education: '#14b8a6',
  career: '#0ea5e9',
  community: '#e11d48',
  tech: '#6366f1',
  food: '#ef4444',
  gaming: '#6366f1',
  film: '#a855f7',
  photo: '#a855f7',
  social: '#db2777',
  outdoor: '#22c55e',
  campus: '#06b6d4',
  charity: '#15803d',
  activism: '#f97316',
  religion: '#4f46e5',
  fun: '#f59e0b',
  general: '#6b7280',
};

const iconSizeLegend = 14;
const strokeWidthLegend = 2;

/** Legacy exact labels (sidebar categories) — colors from same legend palette */
const icons = {
  'All Clubs': Globe,
  'Arts & Culture': Palette,
  Sports: Trophy,
  Volunteering: HeartHandshake,
  'Social & Community': Users,
  Business: Briefcase,
  Games: Gamepad2,
  Travel: Plane,
  International: InternationalIcon,
  'Hobbies & Crafts': Scissors,
  'Learning & Discussion': BookOpen,
  'Faith & Spirituality': Heart,
  'Politics & Civics': Landmark,
};

const iconColors = {
  'All Clubs': EVENT_LEGEND_COLORS.campus,
  'Arts & Culture': EVENT_LEGEND_COLORS.arts,
  Sports: EVENT_LEGEND_COLORS.sports,
  Volunteering: EVENT_LEGEND_COLORS.community,
  'Social & Community': EVENT_LEGEND_COLORS.career,
  Business: EVENT_LEGEND_COLORS.career,
  Games: EVENT_LEGEND_COLORS.gaming,
  Travel: EVENT_LEGEND_COLORS.campus,
  International: EVENT_LEGEND_COLORS.campus,
  'Hobbies & Crafts': EVENT_LEGEND_COLORS.arts,
  'Learning & Discussion': EVENT_LEGEND_COLORS.education,
  'Faith & Spirituality': EVENT_LEGEND_COLORS.religion,
  'Politics & Civics': EVENT_LEGEND_COLORS.activism,
};

/**
 * Keyword rules: icon + color from EVENT_LEGEND_COLORS only (matches event filter chips).
 * More-specific rows first. Coffee before generic food.
 */
const KEYWORD_RULES = [
  { kws: ['application develop', 'app develop', 'software develop', 'web develop', 'programming', 'coding', 'developer', 'computer science', 'hackathon', 'full stack', 'frontend', 'backend', 'devops', 'data science', 'machine learning', 'artificial intelligence'], icon: Code2, color: EVENT_LEGEND_COLORS.tech },
  { kws: ['book read', 'book reading', 'reading', 'literature', 'novel', 'author', 'poetry', 'writing'], icon: BookOpen, color: EVENT_LEGEND_COLORS.education },
  { kws: ['coffee', 'tea', 'cafe'], icon: Coffee, color: EVENT_LEGEND_COLORS.food },
  { kws: ['baseball', 'softball', 'cricket'], icon: Medal, color: EVENT_LEGEND_COLORS.sports },
  { kws: ['basketball', 'volleyball', 'soccer', 'football', 'rugby', 'hockey', 'lacrosse', 'tennis', 'golf', 'badminton', 'pickleball'], icon: Trophy, color: EVENT_LEGEND_COLORS.sports },
  { kws: ['athletic', 'athletics', 'track', 'field', 'marathon', 'running', 'cross country'], icon: Activity, color: EVENT_LEGEND_COLORS.sports },
  { kws: ['fitness', 'gym', 'workout', 'yoga', 'pilates', 'crossfit', 'weight', 'strength'], icon: Dumbbell, color: EVENT_LEGEND_COLORS.sports },
  { kws: ['academic', 'scholar', 'honor', 'debate', 'lecture', 'tutor', 'homework', 'study'], icon: GraduationCap, color: EVENT_LEGEND_COLORS.education },
  { kws: ['science', 'biology', 'chemistry', 'physics', 'astronomy', 'research lab'], icon: Microscope, color: EVENT_LEGEND_COLORS.education },
  { kws: ['math', 'calculus', 'statistics', 'algebra'], icon: Calculator, color: EVENT_LEGEND_COLORS.education },
  { kws: ['engineer', 'robotics', 'mechanical', 'electrical', 'civil engineer'], icon: Cpu, color: EVENT_LEGEND_COLORS.tech },
  { kws: ['medicine', 'medical', 'nursing', 'pre-med', 'health', 'pharmacy', 'dental'], icon: Stethoscope, color: EVENT_LEGEND_COLORS.community },
  { kws: ['law', 'legal', 'pre-law', 'moot'], icon: Landmark, color: EVENT_LEGEND_COLORS.career },
  { kws: ['business', 'finance', 'marketing', 'entrepreneur', 'startup', 'invest'], icon: Briefcase, color: EVENT_LEGEND_COLORS.career },
  { kws: ['career', 'internship', 'resume', 'networking professional', 'recruit'], icon: UserRound, color: EVENT_LEGEND_COLORS.career },
  { kws: ['art', 'drawing', 'painting', 'sketch', 'sculpture', 'design', 'graphic'], icon: Paintbrush, color: EVENT_LEGEND_COLORS.arts },
  { kws: ['photo', 'photography', 'videography', 'film'], icon: Camera, color: EVENT_LEGEND_COLORS.photo },
  { kws: ['music', 'band', 'orchestra', 'choir', 'singing', 'piano', 'guitar', 'dj'], icon: Music, color: EVENT_LEGEND_COLORS.music },
  { kws: ['theater', 'theatre', 'drama', 'acting', 'improv', 'musical'], icon: Theater, color: EVENT_LEGEND_COLORS.microphone },
  { kws: ['dance', 'ballet', 'hip hop dance'], icon: Sparkles, color: EVENT_LEGEND_COLORS.music },
  { kws: ['movie', 'cinema', 'screening'], icon: Clapperboard, color: EVENT_LEGEND_COLORS.film },
  { kws: ['game', 'gaming', 'esports', 'board game', 'video game'], icon: Gamepad2, color: EVENT_LEGEND_COLORS.gaming },
  { kws: ['food', 'cooking', 'culinary', 'baking', 'wine', 'beer'], icon: Utensils, color: EVENT_LEGEND_COLORS.food },
  { kws: ['environment', 'sustainability', 'climate', 'garden', 'outdoor', 'hiking', 'camping', 'nature'], icon: Trees, color: EVENT_LEGEND_COLORS.outdoor },
  { kws: ['adventure', 'climb', 'mountain', 'trail', 'kayak'], icon: Mountain, color: EVENT_LEGEND_COLORS.outdoor },
  { kws: ['travel', 'abroad', 'exchange'], icon: Plane, color: EVENT_LEGEND_COLORS.campus },
  { kws: ['language', 'linguistic', 'esl', 'french', 'spanish', 'chinese', 'japanese', 'korean'], icon: Languages, color: EVENT_LEGEND_COLORS.education },
  { kws: ['international', 'cultural', 'multicultural', 'heritage', 'diaspora'], icon: InternationalIcon, color: EVENT_LEGEND_COLORS.campus },
  { kws: ['fundraiser', 'gala', 'benefit'], icon: HeartHandshake, color: EVENT_LEGEND_COLORS.charity },
  { kws: ['volunteer', 'community service', 'nonprofit', 'charity', 'outreach'], icon: HeartHandshake, color: EVENT_LEGEND_COLORS.community },
  { kws: ['advocacy', 'activist', 'activism', 'social justice', 'equity', 'rights'], icon: Megaphone, color: EVENT_LEGEND_COLORS.activism },
  { kws: ['politic', 'government', 'policy', 'civic', 'election'], icon: Vote, color: EVENT_LEGEND_COLORS.activism },
  { kws: ['faith', 'religion', 'spiritual', 'christian', 'muslim', 'jewish', 'buddhist', 'ministry', 'worship'], icon: Church, color: EVENT_LEGEND_COLORS.religion },
  { kws: ['journalism', 'news', 'media', 'broadcast'], icon: Newspaper, color: EVENT_LEGEND_COLORS.general },
  { kws: ['creative writ', 'fiction', 'journal'], icon: PenLine, color: EVENT_LEGEND_COLORS.arts },
  { kws: ['diy', 'woodwork', 'craft', 'knitting', 'sewing', 'maker'], icon: Hammer, color: EVENT_LEGEND_COLORS.arts },
  { kws: ['automotive', 'car', 'vehicle', 'motor'], icon: Car, color: EVENT_LEGEND_COLORS.general },
  { kws: ['cycling', 'bike', 'bicycle'], icon: Bike, color: EVENT_LEGEND_COLORS.sports },
  { kws: ['ski', 'snowboard', 'snow'], icon: Snowflake, color: EVENT_LEGEND_COLORS.outdoor },
  { kws: ['swim', 'rowing', 'sailing', 'surf', 'ocean', 'marine'], icon: Waves, color: EVENT_LEGEND_COLORS.campus },
  { kws: ['boat', 'sail'], icon: Sailboat, color: EVENT_LEGEND_COLORS.campus },
  { kws: ['fish', 'aquarium', 'angling'], icon: Fish, color: EVENT_LEGEND_COLORS.campus },
  { kws: ['animal', 'pet', 'dog', 'cat', 'wildlife', 'vet'], icon: Dog, color: EVENT_LEGEND_COLORS.fun },
  { kws: ['flower', 'botany', 'plant', 'horticulture'], icon: Flower2, color: EVENT_LEGEND_COLORS.outdoor },
  { kws: ['astronomy', 'space', 'rocket', 'aerospace'], icon: Rocket, color: EVENT_LEGEND_COLORS.tech },
  { kws: ['idea', 'innovation', 'entrepreneurship', 'pitch'], icon: Lightbulb, color: EVENT_LEGEND_COLORS.fun },
  { kws: ['comedy', 'standup', 'open mic'], icon: Mic2, color: EVENT_LEGEND_COLORS.microphone },
  { kws: ['party', 'social', 'mixer', 'networking social'], icon: PartyPopper, color: EVENT_LEGEND_COLORS.social },
  { kws: ['fashion', 'style', 'modeling', 'textile'], icon: ShoppingBag, color: EVENT_LEGEND_COLORS.arts },
  { kws: ['history', 'archaeology', 'museum'], icon: Landmark, color: EVENT_LEGEND_COLORS.general },
  { kws: ['philosophy', 'ethics'], icon: BookOpen, color: EVENT_LEGEND_COLORS.education },
  { kws: ['psychology', 'mental health', 'wellness group'], icon: Heart, color: EVENT_LEGEND_COLORS.community },
  { kws: ['club', 'student org', 'organization'], icon: Users, color: EVENT_LEGEND_COLORS.career },
];

function normalizeType(type) {
  return String(type || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function resolveIconAndColor(type) {
  const raw = String(type || '').trim();
  if (!raw) return { Icon: Users, color: EVENT_LEGEND_COLORS.general };

  if (icons[raw]) {
    return { Icon: icons[raw], color: iconColors[raw] || EVENT_LEGEND_COLORS.general };
  }

  const lower = normalizeType(raw);
  for (const key of Object.keys(icons)) {
    if (key.toLowerCase() === lower) {
      return { Icon: icons[key], color: iconColors[key] || EVENT_LEGEND_COLORS.general };
    }
  }

  for (const rule of KEYWORD_RULES) {
    if (rule.kws.some((kw) => lower.includes(kw))) {
      return { Icon: rule.icon, color: rule.color };
    }
  }

  return { Icon: Users, color: EVENT_LEGEND_COLORS.general };
}

/**
 * @param {'legend' | 'outline'} variant — legend = colored swatch + white icon (matches event filter); outline = gray line icon only
 */
export function HobbyFilterIcon({ type, className = '', variant = 'legend', outline, ...props }) {
  const effectiveVariant = outline === true && variant === 'legend' ? 'outline' : variant;
  const { Icon, color } = useMemo(() => resolveIconAndColor(type), [type]);
  if (!Icon) return null;

  if (effectiveVariant === 'outline') {
    return (
      <span
        className={`inline-flex items-center justify-center shrink-0 w-5 h-5 text-gray-500 dark:text-gray-400 ${className}`}
        {...props}
      >
        <Icon size={18} strokeWidth={1.5} fill="none" />
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center justify-center shrink-0 w-6 h-6 rounded-md ${className}`}
      style={{
        backgroundColor: color,
        border: `2px solid ${color}`,
        boxShadow: '0 6px 16px rgba(15,23,42,0.35)',
      }}
      {...props}
    >
      <Icon
        size={iconSizeLegend}
        strokeWidth={strokeWidthLegend}
        color="#ffffff"
        stroke="#ffffff"
        fill="none"
        className="text-white"
      />
    </span>
  );
}

export default HobbyFilterIcon;
